"use client"

/**
 * ZIP threshold alert signup card ("Email me when pre-emergent time starts
 * in 30301"). Posts to /api/soil-alerts/subscribe (double opt-in). Also owns
 * the post-confirm success card: /api/soil-alerts/confirm redirects back to
 * the map page with ?subscribed=1&zip=..., which this component renders as
 * the post-action peak state.
 *
 * Reads useSearchParams, so mount inside a <Suspense> boundary to keep the
 * host page statically rendered.
 */

import { useState } from "react"
import { useSearchParams } from "next/navigation"
import { Bell, CheckCircle2, Loader2 } from "lucide-react"
import { trackEvent } from "@/lib/analytics"

const ZIP_RE = /^\d{5}$/

export function SoilAlertSignupCard() {
  const searchParams = useSearchParams()
  const confirmedZip = searchParams.get("subscribed") === "1" ? searchParams.get("zip") : null
  const unsubscribed = searchParams.get("unsubscribed")

  const [zip, setZip] = useState("")
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<"idle" | "submitting" | "sent" | "error">("idle")
  const [error, setError] = useState<string | null>(null)

  // Post-confirm success state (arrived via the confirm email redirect).
  if (confirmedZip && ZIP_RE.test(confirmedZip)) {
    return (
      <div id="alerts" className="rounded-lg border border-green-200 bg-green-50 p-5 animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-700" />
          <div>
            <h2 className="text-lg font-semibold text-green-900">
              You&apos;re set for {confirmedZip}.
            </h2>
            <p className="mt-1 text-sm text-green-800">
              We check soil temperatures for your ZIP every day. The moment they cross a lawn-care
              trigger like the pre-emergent window, you&apos;ll get one email, timed to the day it
              happens.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (status === "sent") {
    return (
      <div id="alerts" className="rounded-lg border border-green-200 bg-green-50 p-5 animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-700" />
          <div>
            <h2 className="text-lg font-semibold text-green-900">Almost there.</h2>
            <p className="mt-1 text-sm text-green-800">
              If this address is new for {zip ? `${zip} alerts` : "these alerts"}, a confirmation
              email is on its way. Click the button inside and your alerts are live. Already
              confirmed before? You are all set, no new email needed.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const zipValid = ZIP_RE.test(zip)
  const ctaZip = zipValid ? zip : "your zip"

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (status === "submitting") return
    setStatus("submitting")
    setError(null)
    try {
      const res = await fetch("/api/soil-alerts/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, zipCode: zip }),
      })
      const data = (await res.json().catch(() => null)) as { error?: string } | null
      if (!res.ok) {
        setStatus("error")
        setError(data?.error || "Something went wrong. Please try again.")
        return
      }
      setStatus("sent")
      trackEvent("soil_alert_signup_submitted", { zip_prefix: zip.slice(0, 3) })
    } catch {
      setStatus("error")
      setError("Something went wrong. Please try again.")
    }
  }

  return (
    <div id="alerts" data-calc-ignore="" className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {unsubscribed === "1" && (
        <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          You&apos;re unsubscribed. No more soil temperature alerts will be sent to that address.
          Changed your mind? Sign up again below.
        </div>
      )}
      {unsubscribed === "invalid" && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          That unsubscribe link didn&apos;t work (it may be old or already used). If you keep
          getting alerts, use the link in the most recent email.
        </div>
      )}
      <div className="flex items-center gap-2">
        <Bell className="h-5 w-5 text-green-700" />
        <h2 className="text-lg font-semibold text-gray-900">
          Never miss the pre-emergent window again
        </h2>
      </div>
      <p className="mt-1 text-sm text-gray-600">
        We watch the soil for you. One email the day soil in your ZIP crosses 55°F, the crabgrass
        germination trigger. That&apos;s your signal to get pre-emergent down. No newsletter,
        nothing else.
      </p>
      <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={5}
          placeholder="ZIP code"
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
          aria-label="ZIP code"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-600 focus:outline-none sm:w-28"
          required
        />
        <input
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Email address"
          className="w-full flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-600 focus:outline-none"
          required
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-green-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-800 active:scale-[0.98] disabled:opacity-60"
        >
          {status === "submitting" && <Loader2 className="h-4 w-4 animate-spin" />}
          Email me when crabgrass season starts in {ctaZip}
        </button>
      </form>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      <p className="mt-2 text-xs text-gray-500">
        At most a few emails a year, and one click to unsubscribe.
      </p>
    </div>
  )
}
